import React, { useMemo } from 'react';
import Table from 'apollo-react/components/Table';
import Button from 'apollo-react/components/Button';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Typography from '../StyledTypography';

// Matches the fixed limit useLinkedSubmissions requests per fetch.
const PAGE_SIZE = 20;

const LinkedSubmissionsView = ({
  data = [],
  total = 0,
  page = 1,
  onPageChange,
  loading,
  error,
  detailPathPrefix = '/admin/review',
}) => {
  const columns = useMemo(
    () => [
      {
        header: 'Request Name',
        accessor: 'study_name',
        customCell: ({ row }) => row.study_name || row.generated_id || '-',
      },
      {
        header: 'Submission Type',
        accessor: 'submission_type',
        customCell: ({ row }) =>
          row.submission_type || row.study_name || row.generated_id,
      },
      {
        header: 'Submitted By',
        accessor: 'submitted_by',
        customCell: ({ row }) => row.submitted_by || '-',
      },
      {
        header: 'Submission Date',
        accessor: 'date_started',
        customCell: ({ row }) =>
          row.date_started
            ? new Date(row.date_started).toLocaleDateString()
            : '-',
      },
      {
        header: 'Status',
        accessor: 'status',
        customCell: ({ row }) => row.status || 'In Progress',
      },
      {
        header: 'Rejection Reason',
        accessor: 'rejection_reason',
        customCell: ({ row }) => row.rejection_reason || '-',
      },
      {
        header: 'Action',
        accessor: 'id',
        customCell: ({ row }) => (
          <a
            href={`${detailPathPrefix}/${row.id}`}
            target="_blank"
            rel="noreferrer"
          >
            Review
          </a>
        ),
      },
    ],
    [detailPathPrefix]
  );

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!data.length)
    return (
      <Typography>
        No approved child-workflow requests are linked to this Parent Request
        yet.
      </Typography>
    );

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  return (
    <>
      {/*
        `data` is already the current server page's rows (useLinkedSubmissions
        fetches one page at a time), but apollo-react's Table always re-slices
        whatever `rows` it's given using its own (0-indexed) `page`/`rowsPerPage`
        state - it has no server-paginated mode. Passing our 1-indexed page
        straight through made it slice past our already-small result set,
        showing an empty table with a garbled "Items X-Y of Z" footer.
        hidePagination stops it from re-slicing or rendering that footer; we
        drive paging ourselves below instead.
      */}
      <Table columns={columns} rows={data} rowId="id" hidePagination />
      {total > PAGE_SIZE && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginTop: 12,
          }}
        >
          <Button
            variant="secondary"
            size="small"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </Button>
          <Typography>
            Page {page} of {totalPages} ({total} items)
          </Typography>
          <Button
            variant="secondary"
            size="small"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </>
  );
};

export default LinkedSubmissionsView;
